import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Upload as UploadIcon
} from '@mui/icons-material';
import { useMsal } from '@azure/msal-react';
import { campaignsAPI } from '../services/api';

const statusColors = {
  advertising: 'info',
  screening: 'warning',
  shortlisting: 'secondary',
  offer_made: 'primary',
  filled: 'success',
  closed: 'default'
};

const statusIcons = {
  advertising: <UploadIcon />,
  screening: <PeopleIcon />,
  shortlisting: <PendingIcon />,
  offer_made: <CheckCircleIcon />,
  filled: <CheckCircleIcon />,
  closed: <BusinessIcon />
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    title: '',
    roleText: '',
    status: 'advertising'
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      const response = await campaignsAPI.getAll();
      setCampaigns(response.data);
    } catch (error) {
      console.error('Failed to load campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = async () => {
    if (!newCampaign.title.trim() || !newCampaign.roleText.trim()) return;

    setCreating(true);
    try {
      await campaignsAPI.create(newCampaign);
      setCreateDialogOpen(false);
      setNewCampaign({ title: '', roleText: '', status: 'advertising' });
      loadCampaigns();
    } catch (error) {
      console.error('Failed to create campaign:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleCampaignClick = (campaignId) => {
    navigate(`/campaigns/${campaignId}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Welcome back, {accounts[0]?.name || 'User'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your recruitment campaigns and candidates
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
          size="large"
        >
          New Campaign
        </Button>
      </Box>

      {/* Stats Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <BusinessIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Active Campaigns</Typography>
              </Box>
              <Typography variant="h4">{campaigns.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PeopleIcon color="secondary" sx={{ mr: 1 }} />
                <Typography variant="h6">Total Applications</Typography>
              </Box>
              <Typography variant="h4">
                {campaigns.reduce((sum, c) => sum + (c.metrics?.applications || 0), 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="h6">Parsed CVs</Typography>
              </Box>
              <Typography variant="h4">
                {campaigns.reduce((sum, c) => sum + (c.metrics?.parsed || 0), 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PendingIcon color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6">Pending Review</Typography>
              </Box>
              <Typography variant="h4">
                {campaigns.reduce((sum, c) => sum + ((c.metrics?.applications || 0) - (c.metrics?.parsed || 0)), 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Campaigns Grid */}
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        Your Campaigns
      </Typography>

      <Grid container spacing={3}>
        {campaigns.map((campaign) => (
          <Grid item xs={12} sm={6} md={4} key={campaign._id}>
            <Card
              sx={{
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}
              onClick={() => handleCampaignClick(campaign._id)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <BusinessIcon />
                  </Avatar>
                  <Chip
                    label={campaign.status}
                    color={statusColors[campaign.status]}
                    size="small"
                    icon={statusIcons[campaign.status]}
                  />
                </Box>

                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  {campaign.title}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {campaign.roleText.length > 100
                    ? `${campaign.roleText.substring(0, 100)}...`
                    : campaign.roleText
                  }
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Applications: {campaign.metrics?.applications || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Parsed: {campaign.metrics?.parsed || 0}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(campaign.createdAt).toLocaleDateString()}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {/* Empty state */}
        {campaigns.length === 0 && (
          <Grid item xs={12}>
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No campaigns yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Create your first recruitment campaign to get started
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
              >
                Create Campaign
              </Button>
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Create Campaign Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Campaign</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Campaign Title"
            fullWidth
            variant="outlined"
            value={newCampaign.title}
            onChange={(e) => setNewCampaign({ ...newCampaign, title: e.target.value })}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            margin="dense"
            label="Role Description"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={newCampaign.roleText}
            onChange={(e) => setNewCampaign({ ...newCampaign, roleText: e.target.value })}
            placeholder="Describe the role, requirements, and responsibilities..."
            sx={{ mb: 2 }}
          />
          <TextField
            select
            margin="dense"
            label="Status"
            fullWidth
            variant="outlined"
            value={newCampaign.status}
            onChange={(e) => setNewCampaign({ ...newCampaign, status: e.target.value })}
          >
            <MenuItem value="advertising">Advertising</MenuItem>
            <MenuItem value="screening">Screening</MenuItem>
            <MenuItem value="shortlisting">Shortlisting</MenuItem>
            <MenuItem value="offer_made">Offer Made</MenuItem>
            <MenuItem value="filled">Filled</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateCampaign}
            variant="contained"
            disabled={creating || !newCampaign.title.trim() || !newCampaign.roleText.trim()}
          >
            {creating ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Dashboard;
